import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { createCellCssClassRules, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { useMST3020_R01Store } from "./Store_MST3020_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { MST3020_R01DetailDto } from "./Dto_MST3020_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { useCodeSearch } from "src/hooks/useCodeSearch";

const COLUMN_STATE_KEY = 'MST3020_R01-detail-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<MST3020_R01DetailDto> | null>;
	defaultMST3020_R01DetailRow: MST3020_R01DetailDto;
}

export const DetailTable_MST3020_R01 = (props: Props) => {

    const { gridRef, defaultMST3020_R01DetailRow } = props;

    const { 
            MST3020_R01DetailList, setMST3020_R01DetailList,setEditMST3020_R01Detail,
            selectedMST3020_R01DetailList, removeSelectedMST3020_R01DetailList, addSelectedMST3020_R01DetailList, initSelectedMST3020_R01DetailList,
            deletedMST3020_R01DetailList,  addDeletedMST3020_R01DetailList, 
            insertMST3020_R01DetailList, updateMST3020_R01DetailList, removeMST3020_R01DetailList,
            detailColumnState, setDetailColumnState,
            detailScrollTop, setDetailScrollTop,
            originalMST3020_R01DetailList, 
        } = useMST3020_R01Store();
    const { setCodeSearchModalProps } = useUiuxStore();
    const [clickable, setClickable] = useState(true);

    const gridWrapperRef = useRef<HTMLDivElement>(null);

    // === DetailTable 내장 핸들러 로직 ===
    // 공통 코드 검색 훅 사용 (공통 모달 방식)
const { searchExactCode } = useCodeSearch({});

    // 모듈별 설정
    const MST3020R01Config = createModuleConfig.MST3020();

    // 공통 검색 함수를 설정에 주입 (공정코드, 창고코드 카테고리)
    const fieldConfigWithSearch = {
        ...MST3020R01Config.fieldConfig,
        codeSearchFields: {
            ...MST3020R01Config.fieldConfig.codeSearchFields,
            workplaceCd: {
                ...MST3020R01Config.fieldConfig.codeSearchFields!.workplaceCd,
                searchFunction: (code: string, onSuccess?: (item: any) => void) => 
                    searchExactCode('workplace', code, onSuccess)
            },
            warehouseCd: {
                ...MST3020R01Config.fieldConfig.codeSearchFields!.warehouseCd,
                searchFunction: (code: string, onSuccess?: (item: any) => void) => 
                    searchExactCode('warehouse', code, onSuccess)
            }
        }
    };

    // MST3020 전용 금액 계산 함수 (실제로는 계산하지 않음)
    const calculateAmount = (data: MST3020_R01DetailDto): MST3020_R01DetailDto => {
        return data; // MST3020는 금액 계산이 없으므로 그대로 반환
    };

    // 공통 훅 사용
    const detailTableHandlers = useCommonDetailTableHandlers<MST3020_R01DetailDto>({
        detailList: MST3020_R01DetailList,
        updateDetailList: updateMST3020_R01DetailList,
        originalDetailList: originalMST3020_R01DetailList,
        setCodeSearchModalProps,
        gridRef,
        fieldConfig: fieldConfigWithSearch,
        codeSearchConfig: MST3020R01Config.codeSearchConfig,
        calculateAmount,
    });

    const {
        getRowId,
        createRowClassRules,
		createCellClassRules,
        paddedGridData,
        renderIndexCell,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: MST3020_R01DetailList,
        defaultGridData: defaultMST3020_R01DetailRow,
        selectedRow: selectedMST3020_R01DetailList,
        originalGridData: originalMST3020_R01DetailList,
        updateRow: (index: number, data: any) => updateMST3020_R01DetailList(index, data),
    });

    const headerNames: Record<string, string> = {
        rowIndex: "",
        workplaceCd: '공정코드',
        workplaceNm: '공정명',
		workplaceSeq: '작업순서',
        cycletime: 'CycleTime',
        cavity: 'Cavity',
        boxQty: 'Box량',
        warehouseCd: '창고코드',
        warehouseNm: '창고명',
        remark: '비고',
    }

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['workplaceNm', 'warehouseNm',],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],
		pointerCells: ['workplaceCd', 'workplaceSeq', 'cycletime', 'cavity', 'boxQty', 'standardSheet', 'standardSheet2', 'warehouseCd', 'remark', ],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);
	
	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<MST3020_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedMST3020_R01DetailList,
            deletedList: deletedMST3020_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedMST3020_R01DetailList, deletedMST3020_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
    ]);

    /**
     * 편집 가능한 컬럼 목록 정의
     * 사용자가 직접 입력/수정할 수 있는 필드들을 지정
     */
    const defaultEditableColumns = ['workplaceCd', 'workplaceNm', 'workplaceSeq', 'cycletime', 'cavity', 'boxQty', 'warehouseCd', 'warehouseNm', 'remark'];

    /**
     * 기본 상세 테이블 이벤트 핸들러 훅
     * 
     * 주요 기능:
     * - 키다운 네비게이션 (방향키 이동)
     * - 셀 클릭/키다운 이벤트 처리
     * 
     * @param detailList - 상세 데이터 목록
     * @param editableColumns - 편집 가능한 컬럼 목록
     */
    const cellEventHandlers = useDetailTableEvents({
        detailList: MST3020_R01DetailList,
        editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
    });

    const baseColumnDefs: ColDef<MST3020_R01DetailDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(
            (params: MST3020_R01DetailDto) => renderIndexCell(
                params, 
                (innerParams: MST3020_R01DetailDto) => getRowStyleFlags(
                    innerParams,
                    {
                        deletedList: deletedMST3020_R01DetailList,
                        selectedList: selectedMST3020_R01DetailList,
                        dataList: MST3020_R01DetailList
                    }),
                (rowIndex: number, e: React.MouseEvent) => InsertRowHandler({
                    event: e,
                    dataList: MST3020_R01DetailList,
                    rowData: MST3020_R01DetailList[rowIndex],
                    insertRow: insertMST3020_R01DetailList,
                    defaultRowData: defaultMST3020_R01DetailRow,
                    setClickable
                }),
                (rowData) => RemoveRowHandler( 
                    gridRef,
                    MST3020_R01DetailList, 
                    rowData,
                    setClickable, 
                    removeMST3020_R01DetailList, 
                    (valueIndex) => addDeletedMST3020_R01DetailList([valueIndex.toString()])
                ),
            )
        ),
        {
            headerName: '공정코드', colId: 'workplaceCd', field: 'workplaceCd', width: 77,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '공정명', colId: 'workplaceNm', field: 'workplaceNm', width: 77,
            editable: false,
        },
		{
			headerName: '작업순서', colId: 'workplaceSeq', field: 'workplaceSeq', width: 85,
			editable: true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
			pasteable: true,
			valueParser: (params) => {
				const workplaceSeq = Math.trunc(Number(String(params.newValue ?? '').replace(/,/g, '').trim()));
				return workplaceSeq > 0 ? workplaceSeq : 1;
			},
		},
        {
            headerName: 'CycleTime', colId: 'cycletime', field: 'cycletime', width: 85,
            editable: true,
            comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
            valueFormatter: (params) => params.value?.toLocaleString() ?? '',
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
            valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: 'Cavity', colId: 'cavity', field: 'cavity', width: 85,
            editable: true,
            comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
            valueFormatter: (params) => params.value?.toLocaleString() ?? '',
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
            valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: 'Box량', colId: 'boxQty', field: 'boxQty', width: 85,
            editable: true,
            comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
            valueFormatter: (params) => params.value?.toLocaleString() ?? '',
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
            valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
        },
        {
            headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 105,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
        {
            headerName: '창고명', colId: 'warehouseNm', field: 'warehouseNm', width: 105,
            editable: false,
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 77,
            editable: true,
            pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
        },
    ], [renderIndexCell, deletedMST3020_R01DetailList, selectedMST3020_R01DetailList, MST3020_R01DetailList, insertMST3020_R01DetailList, defaultMST3020_R01DetailRow, gridRef, removeMST3020_R01DetailList, addDeletedMST3020_R01DetailList]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
    [baseColumnDefs]);

    const columnDefs: ColDef<MST3020_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) {
            return baseColumnDefs;
        }
        
        return GridCustomColumnDefs<MST3020_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass
        });
    }, [baseColumnDefs, detailColumnState, headerNames]);

	const handleRowSelection = useRowSelectionHandler({
        dataList: MST3020_R01DetailList,
        idKey: 'valueIndex',
        selectedList: selectedMST3020_R01DetailList,
        deletedList: deletedMST3020_R01DetailList,
        addSelected: addSelectedMST3020_R01DetailList,
        removeSelected: removeSelectedMST3020_R01DetailList,
        clickable, setClickable
    });

	const handleRowClick = useCallback((event: RowClickedEvent<MST3020_R01DetailDto>) => {
		const isEditableRow = event.node.rowPinned !== 'bottom'
            && event.data && clickable
			&& !deletedMST3020_R01DetailList.includes(event.data.valueIndex.toString());

		handleRowSelection(event);

		if (!isEditableRow || !event.data) {
			return;
		}

		setEditMST3020_R01Detail(event.data);
	}, [
		clickable,
		deletedMST3020_R01DetailList,
		handleRowSelection,
		setEditMST3020_R01Detail,
	]);

    // 더블클릭 핸들러 (코드 검색용)
    const handleCellDoubleClick = (e: any) => {
        const field = e.colDef.field;
        const data = e.data;
        
        if (field === 'workplaceCd') {
            detailTableHandlers.settingCodeSearchModalProps(
                '공정코드',
                data.workplaceCd || '',
                false,
                'workplaceCd',
                data,
                data.valueIndex
            );
        } else if (field === 'warehouseCd') {
            detailTableHandlers.settingCodeSearchModalProps(
                '창고코드',
                data.warehouseCd || '',
                false,
                'warehouseCd',
                data,
                data.valueIndex
            );
        }
    };


    const handlePostSort = useCallback((params: PostSortRowsParams<MST3020_R01DetailDto>) => {
        postSortHandler(params, MST3020_R01DetailList, originalMST3020_R01DetailList, setMST3020_R01DetailList);
    }, [MST3020_R01DetailList, originalMST3020_R01DetailList, setMST3020_R01DetailList]);

    const pinnedBottomRowData = useMemo(() => {

        return [{
            workplaceCd: MST3020_R01DetailList.length.toLocaleString() + "건",
        }];      
    }, [MST3020_R01DetailList]);

    return (
        <div className="w-full h-full flex flex-col gap-[4px]">
                    <GridContextWrapper<MST3020_R01DetailDto>
                        gridWrapperRef={gridWrapperRef}
                        gridRef={gridRef}
                        paddedGridData={paddedGridData}
                        enableCellCalculation
                        setPaddedGridData={setMST3020_R01DetailList}
                        initSelected={initSelectedMST3020_R01DetailList}
						defaultRowData={defaultMST3020_R01DetailRow}
                        baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
                        defaultColumnState={defaultColumnState}
                        setColumnState={setDetailColumnState}
                        localStorageKey={COLUMN_STATE_KEY}
                        menuItems={undefined}
                    >
                        <Grid_Excel<MST3020_R01DetailDto>
                            ref={gridRef}
                            rowData={paddedGridData}
                            getRowId={getRowId}
                            columnDefs={columnDefs}
                            defaultColDef={cellCssDefaultColDef}

                            disableBuiltinCellSelection={true} // 내장 셀 선택 비활성화 (새로운 훅 사용)
                            columnStateKey={COLUMN_STATE_KEY} // 자동 컬럼 상태 관리 (localStorage)
                            setColumnState={setDetailColumnState}
                            getDefaultColumnState={() => defaultColumnState}
                            scrollTop={detailScrollTop}
                            setScrollTop={setDetailScrollTop}

                            // 행 스타일 규칙 (선택/삭제/수정 상태에 따른 색상)
                            rowClassRules={createRowClassRules<MST3020_R01DetailDto>({
                                idKey: 'valueIndex',
                                selectedList: selectedMST3020_R01DetailList,
                                deletedList: deletedMST3020_R01DetailList
                            })}

                            // 이벤트 핸들러들
                            onRowClicked={handleRowClick}                                    // 행 클릭 (선택)
                            onCellClicked={cellEventHandlers.handleCellClick}               // 셀 클릭 (편집 시작)
                            onCellDoubleClicked={handleCellDoubleClick}                     // 셀 더블클릭 (코드 검색)
                            onCellKeyDown={cellEventHandlers.handleCellKeyDown}             // 셀 키다운 (네비게이션)
                            onCellValueChanged={detailTableHandlers.handleCellChange}
                            onCellEditingStopped={detailTableHandlers.handleCellEditingStopped}
                            pinnedBottomRowData={pinnedBottomRowData}
                            
                            // 정렬 후 처리
                            onPostSortRows={handlePostSort}
                        />
                    </GridContextWrapper>
        </div>
    )
}
